/**
 * «Subir el DNI», junto a la fecha de nacimiento del formulario del inquilino.
 *
 * Es de donde sale esa fecha: el campo no tiene valor por defecto y el contrato no la trae. Se
 * suelta la foto o el PDF del DNI, NIE o pasaporte, el servidor lo lee con OCR LOCAL —no sale a
 * ningún servicio de terceros— y el panel rellena la fecha. Quien rellena es el panel; aquí solo
 * se sube el fichero y se cuenta qué ha salido.
 *
 * COMO EL CONTRATO, SE GUARDA AL GUARDAR, NO AL LEERLO: el panel se queda con el último documento
 * soltado y, al crear o guardar la ficha, lo añade a sus documentos. Se guarda aunque no se haya
 * encontrado la fecha: sigue siendo su DNI.
 */
import { useRef, useState } from 'react';
import type { IdDocumentReadingResponse } from '@naar/shared';
import { normalizeIdDocument } from '@naar/shared';
import { ApiError, api } from '../../api/client';
import { Icon } from '../../components/Icon';
import { toast } from '../../components/feedback/Toaster';
import { formatShortDate } from '../../lib/format';
import styles from './ContractReader.module.css';

interface IdDocumentReaderProps {
  /** La última lectura. La guarda el panel, que es quien la reinicia. */
  lectura: IdDocumentReadingResponse | null;
  /** El documento que se guardará al guardar la ficha. */
  documento: File | null;
  /** El número de documento que dice el formulario, para avisar si el DNI es de otra persona. */
  numeroDelFormulario: string;
  onRead: (lectura: IdDocumentReadingResponse, fichero: File) => void;
}

const ORIGEN: Record<NonNullable<IdDocumentReadingResponse['source']>, string> = {
  mrz: 'de la banda de abajo del reverso',
  front: 'del anverso',
};

export function IdDocumentReader({ lectura, documento, numeroDelFormulario, onRead }: IdDocumentReaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [leyendo, setLeyendo] = useState(false);

  async function leer(file: File | undefined) {
    if (!file) return;
    setLeyendo(true);
    try {
      onRead(await api.readTenantIdDocument(file), file);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido leer el documento.');
    } finally {
      setLeyendo(false);
    }
  }

  const numero = normalizeIdDocument(numeroDelFormulario);
  const deOtro = lectura?.idDocument && numero && lectura.idDocument !== numero ? lectura.idDocument : null;

  return (
    <div className={styles.reader}>
      <div
        className={over ? `${styles.drop} ${styles.dropOver}` : styles.drop}
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          void leer(event.dataTransfer.files[0]);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          className={styles.dropInput}
          onChange={(event) => {
            void leer(event.target.files?.[0]);
            // Se limpia para que volver a elegir el MISMO fichero dispare el evento otra vez.
            event.target.value = '';
          }}
        />
        <button
          type="button"
          className={styles.dropButton}
          onClick={() => inputRef.current?.click()}
          disabled={leyendo}
        >
          <Icon name="file" size={15} strokeWidth={2} />
          {leyendo
            ? 'Leyendo el documento…'
            : lectura
              ? 'Leer otro documento'
              : 'Suelta aquí el DNI, NIE o pasaporte (foto o PDF) para leer la fecha de nacimiento'}
        </button>
      </div>

      {lectura && (
        <div className={styles.result} role="status">
          <p>
            {lectura.birthDate ? (
              <>
                <Icon name="check" size={14} strokeWidth={2.4} /> Fecha de nacimiento:{' '}
                {formatShortDate(lectura.birthDate)}, {ORIGEN[lectura.source ?? 'front']}. Repásala.
              </>
            ) : (
              'No se ha encontrado la fecha de nacimiento.'
            )}
          </p>
          {documento && (
            <p className={styles.saved}>
              <Icon name="file-lines" size={14} /> Al guardar se añadirá a sus documentos: «{documento.name}».
            </p>
          )}
        </div>
      )}

      {[
        ...(deOtro
          ? [`El documento subido es el ${deOtro} y el formulario dice ${numero}: comprueba que es el suyo.`]
          : []),
        ...(lectura?.warnings ?? []),
      ].map((aviso) => (
        <div key={aviso} className={styles.warning}>
          <Icon name="info" size={15} />
          <span>{aviso}</span>
        </div>
      ))}
    </div>
  );
}
